import { ConfigProvider, Flex, Typography, Card } from 'antd';
import Icon from '@ant-design/icons';
import { useState } from 'react';
import './AlertCard.css';
import { ReactComponent as MoreSvg } from '../../assets/icons/more.svg';
import { ReactComponent as MoreBlackSvg } from '../../assets/icons/more-black.svg';
import { ReactComponent as AlertCardEmptyDark } from '../../assets/icons/alert-card-empty.svg';
import { ReactComponent as AlertCardEmptyLight } from '../../assets/icons/alert-card-empty-light.svg';
import { useTheme } from '../../context/ThemeContext';
import { DARK } from '../../utilities/constants';
import EmptyCard from '../EmptyCard/EmptyCard';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';

const { Text, Title } = Typography;

const getStripHoverColor = (theme, title, alertColor) => {
  if (theme === DARK) return alertColor;
  if (title === 'SOS Alerts' || title === 'Driver Anomalies') return '#A80003';
  if (title === 'Major Anomalies' || title === 'Major') return '#AD8C00';
  return alertColor;
};

const getDetectedTextColor = (title, theme) => {
  if (title === 'SOS Alerts') {
    return theme === DARK ? '#D0D4D8' : '#43464A';
  }
  return 'var(--detected-text-color, #4e5256)';
};

const AlertCard = ({
  title,
  count,
  incidents,
  alertColor,
  headerIcon,
  headerIconHover,
}) => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const [isCardHovered, setIsCardHovered] = useState(false);
  const cardClass =
    'alertcard-anomaly ' + (title === 'SOS Alerts' && 'alertcard-sos');
  const stripColor = alertColor;
  const stripHoverColor = getStripHoverColor(theme, title, alertColor);

  const handleClick = () => {
    if (title === 'Driver Anomalies')
      navigate(`/driver-monitoring-system`, { state: { card: title } });
    else navigate(`/safety-and-anomaly-monitoring`, { state: { card: title } });
  };
  return (
    <ConfigProvider
      theme={{
        components: {
          Typography: {
            titleMarginTop: '16px',
            titleMarginBottom: '0px',
          },
          Card: {
            padding: 0,
            paddingLG: 0,
          },
        },
      }}
    >
      <Card
        variant="borderless"
        className={`full-height alertcard ${cardClass}`}
        onClick={handleClick}
        onMouseEnter={() => setIsCardHovered(true)}
        onMouseLeave={() => setIsCardHovered(false)}
        style={{
          '--alertcard-strip-color': stripColor,
          '--alertcard-strip-hover-color': stripHoverColor,
        }}
      >
        <div className="alertcard-strip"></div>
        <Flex
          vertical
          className="alertcard-header"
          style={{
            padding: '16px 16px',
          }}
          gap={20}
        >
          <Flex justify="space-between" align="center">
            <Flex gap={8} align="center">
              <Icon
                component={
                  isCardHovered && headerIconHover
                    ? headerIconHover
                    : headerIcon
                }
                style={{
                  fontSize: '24px',
                  color: isCardHovered ? stripHoverColor : stripColor,
                  backgroundColor:
                    theme === DARK && title === 'SOS Alerts'
                      ? '#1a1c1d'
                      : 'transparent',
                }}
                className="header-alert-icon"
              />
              <Title level={5} style={{ margin: '0px 0px' }}>
                {title}
              </Title>
            </Flex>
            <Icon
              component={theme === DARK ? MoreSvg : MoreBlackSvg}
              style={{ fontSize: '24px' }}
            />
          </Flex>
          <Text
            className="alertcard-typography-count"
            style={{
              color:
                title !== 'SOS Alerts' && theme !== DARK
                  ? alertColor
                  : 'inherit',
            }}
          >
            {count}
            <Text
              className="alertcard-typography-detected"
              style={{
                color: getDetectedTextColor(title, theme),
              }}
            >
              Detected
            </Text>
          </Text>
        </Flex>
        <div className="alertcard-content">
          {count === 0 ? (
            <EmptyCard
              image={
                <Icon
                  component={
                    theme === DARK ? AlertCardEmptyDark : AlertCardEmptyLight
                  }
                  height={'5em'}
                  width={'12em'}
                />
              }
              description={
                <Flex vertical>
                  <Text strong>{`Yay! No ${title} alerts to show.`}</Text>
                  <Text
                    style={{ color: '#979EA4' }}
                  >{`When an ${title} is detected, it will reflect here.`}</Text>
                </Flex>
              }
            />
          ) : (
            <Flex vertical gap={12}>
              {incidents.map(item => {
                return (
                  <Flex
                    justify="space-between"
                    key={item?.key}
                    className="alertcard-content-item"
                  >
                    <Flex gap={10} align="center">
                      <Icon component={item.icon} className="status-icon" />
                      <Text className="alertcard-content-name">
                        {item.text}
                      </Text>
                    </Flex>
                    <Text className="alertcard-content-count">
                      {item.count}
                    </Text>
                  </Flex>
                );
              })}
            </Flex>
          )}
        </div>
      </Card>
    </ConfigProvider>
  );
};

AlertCard.propTypes = {
  title: PropTypes.string.isRequired,
  count: PropTypes.number.isRequired,
  incidents: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      icon: PropTypes.elementType,
      text: PropTypes.string,
      count: PropTypes.number,
    })
  ).isRequired,
  alertColor: PropTypes.string.isRequired,
  headerIcon: PropTypes.elementType.isRequired,
  headerIconHover: PropTypes.elementType.isRequired,
};

export default AlertCard;
